
import { Link } from "react-router-dom";
import { Database, Sparkles, CheckCircle2 } from "lucide-react";

interface ChunkInspectorPanelProps {
  chunks: any[];
}

export function ChunkInspectorPanel({ chunks }: ChunkInspectorPanelProps) {
  return (
    <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs space-y-4">
      <div className="border-b border-stone-100 dark:border-stone-800 pb-3 flex items-center justify-between">
        <div className="space-y-0.5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-stone-800 dark:text-stone-200 flex items-center gap-1.5">
            <Database className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span>Chunk Inspector & Vector Embeddings</span>
          </h3>
          <p className="text-[11px] text-stone-400 dark:text-stone-500">
            Segmented by recursive character splitter with 384-dimensional cosine index.
          </p>
        </div>
        <span className="text-xs font-mono font-bold text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 px-2 py-0.5 rounded">
          {chunks.length} Chunks
        </span>
      </div>

      {/* Model Status Card */}
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/80 bg-emerald-50/50 dark:bg-emerald-950/40 p-3 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          <div>
            <span className="font-bold text-emerald-950 dark:text-emerald-300 font-mono">bge-small-en-v1.5</span>
            <span className="text-[11px] text-emerald-800 dark:text-emerald-400 block">
              384 dimensions • Cosine similarity index active
            </span>
          </div>
        </div>
        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white">
          Online
        </span>
      </div>

      {/* Chunks List */}
      <div className="space-y-3 max-h-[500px] overflow-y-scroll [scrollbar-gutter:stable] pr-1">
        {chunks.length === 0 ? (
          <div className="py-12 text-center text-xs text-stone-400 dark:text-stone-500">
            No vector chunks generated yet. Save the document to trigger automatic segmentation.
          </div>
        ) : (
          chunks.map((chunk, idx) => (
            <div
              key={chunk.id || idx}
              className="rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/40 dark:bg-stone-800/40 p-3.5 space-y-2 text-xs"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-stone-800 dark:text-stone-200 text-[11px]">
                    Chunk #{chunk.chunk_index !== undefined ? chunk.chunk_index : idx}
                  </span>
                  {chunk.id && (
                    <Link
                      to={`/knowledge/chunk/${chunk.id}`}
                      className="text-[10px] text-cyan-600 dark:text-cyan-400 hover:underline font-mono transition"
                      title={`Inspect chunk ${chunk.id}`}
                    >
                      inspect →
                    </Link>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-stone-400 dark:text-stone-500">
                    {chunk.char_length || chunk.chunk_text?.length} chars
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.2 rounded font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/80">
                    <CheckCircle2 className="w-2.5 h-2.5" />
                    <span>384-dim</span>
                  </span>
                </div>
              </div>

              <p className="text-stone-700 dark:text-stone-300 leading-relaxed font-sans text-xs bg-white dark:bg-stone-900 p-2.5 rounded border border-stone-100 dark:border-stone-800">
                {chunk.chunk_text}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
